import { VPadStage } from "@/interfaces/virtualPad_types"

/**
 * Any message supported by the VPad P2P protocol
 */
export type VPadP2PMsg =
  | VPadP2PIncomingMsg
  | VPadP2POutgoingMsg

/**
 * Messages received by the host from the remote virtual pad
 */
export type VPadP2PIncomingMsg =
  | VPadP2PInputMsg
  | VPadP2PCommunicationMsg

/**
 * Messages sent by the host to the remote virtual pad
 */
export type VPadP2POutgoingMsg =
  | VPadP2PLeaveMsg
  | VPadP2PEjectMsg
  | VPadP2PPongMsg

/**
 * Main categories supported by the VPad P2P protocol
 */
export enum VPadP2PTypes {
  INPUT = 'input',
  COMMUNICATION = 'communication'
}

/**
 * Input messages received by the host
 */
export type VPadP2PInputMsg =
  | VPadP2PButtonMsg
  | VPadP2PMovementMsg

/**
 * Communication messages received by the host
 */
export type VPadP2PCommunicationMsg =
  | VPadP2PCloseMsg
  | VPadP2PPingMsg
  | VPadP2PStatusMsg

/**
 * Supported input message subtypes.
 */
export enum VPadP2PInputSubtype {
  BUTTON = 'button',
  MOVEMENT = 'movement'
}

/**
 * Supported communication message subtypes
 */
export enum VPadP2PCommunicationSubtype {
  LEAVE = 'leave',
  EJECT = 'eject',
  CLOSE = 'close',
  PING = 'ping',
  PONG = 'pong',
  STATUS = 'status'
}

/**
 * Reports a button state change from the remote virtual pad
 */
export interface VPadP2PButtonMsg {
  type: VPadP2PTypes.INPUT
  subtype: VPadP2PInputSubtype.BUTTON
  data: VPadP2PButtonData
}

/**
 * Reports a joystick movement from the remote virtual pad
 */
export interface VPadP2PMovementMsg {
  type: VPadP2PTypes.INPUT
  subtype: VPadP2PInputSubtype.MOVEMENT
  data: VPadP2PMovementData
}

/**
 * Indicates that the host has manually removed the remote virtual pad
 */
export interface VPadP2PLeaveMsg {
  type: VPadP2PTypes.COMMUNICATION
  subtype: VPadP2PCommunicationSubtype.LEAVE
}

/**
 * Indicates that the host has automatically ejected the remote
 * virtual pad to free a controller slot.
 */
export interface VPadP2PEjectMsg {
  type: VPadP2PTypes.COMMUNICATION
  subtype: VPadP2PCommunicationSubtype.EJECT
}

/**
 * Indicates that the remote virtual pad is closing its connection with the host
 */
export interface VPadP2PCloseMsg {
  type: VPadP2PTypes.COMMUNICATION
  subtype: VPadP2PCommunicationSubtype.CLOSE
}

/**
 * Checks that the remote virtual pad connection is still responsive
 */
export interface VPadP2PPingMsg {
  type: VPadP2PTypes.COMMUNICATION
  subtype: VPadP2PCommunicationSubtype.PING
  sentAt: string
}

/**
 * Responds to a ping from the remote virtual pad
 */
export interface VPadP2PPongMsg {
  type: VPadP2PTypes.COMMUNICATION
  subtype: VPadP2PCommunicationSubtype.PONG
  sentAt: string
}

/**
 * Reports the current status of the remote virtual pad
 */
export interface VPadP2PStatusMsg {
  type: VPadP2PTypes.COMMUNICATION
  subtype: VPadP2PCommunicationSubtype.STATUS
  data: {
    status: VPadStage
  }
}

/**
 * Button identifiers supported by the virtual pad protocol
 */
export enum VPadP2PKeyCodes {
  A = 'a',
  B = 'b',
  X = 'x',
  Y = 'y',
  R1 = 'r1',
  R2 = 'r2',
  R3 = 'r3',
  L1 = 'l1',
  L2 = 'l2',
  L3 = 'l3',
  ARROW_LEFT = 'arrowLeft',
  ARROW_RIGHT = 'arrowRight',
  ARROW_DOWN = 'arrowDown',
  ARROW_UP = 'arrowUp',
  START = 'start',
  SELECT = 'select',
  HOME = 'home'
}

/**
 * Represents the state of a virtual gamepad button
 *
 * The value must be between 0 and 1
 */
export interface VPadP2PButtonData {
  keyCode: VPadP2PKeyCodes
  value: number
}

/**
 * Joysticks supported by the virtual pad protocol
 */
export enum VPadP2PAxis {
  LEFT = 'left',
  RIGHT = 'right'
}

/**
 * Represents the position of a virtual joystick
 *
 * Both values must be between -1 and 1:
 * [horizontal, vertical].
 */
export interface VPadP2PMovementData {
  axis: VPadP2PAxis
  values: [x: number, y: number]
}